/**
 * Detección centralizada del conflicto de concurrencia optimista (HTTP 409 /
 * `OPTIMISTIC_LOCK_VIOLATION`) para CUALQUIER mutación del documento
 * (saveHeader, saveLine, deleteLine, execute-process/transición).
 *
 * El backend responde 409 con un problem-details:
 *   - código `OPTIMISTIC_LOCK_VIOLATION`
 *   - `detail` traducido (`error.row_version_stale` / `document.header_concurrency_conflict`)
 *   - extensiones `row_version_expected` y `row_version_actual`.
 *
 * El adapter por defecto LANZA un error (con `.status` y `.body`) ante un
 * non-ok; otros adapters pueden lanzar con otra forma o devolver la clave
 * reservada `_conflict`. Este helper hace duck-typing sobre lo que se lanza para
 * reconocer el 409 sin acoplarse a la clase `ApiError` concreta.
 */

export const OPTIMISTIC_LOCK_CODE = 'OPTIMISTIC_LOCK_VIOLATION';

/**
 * Código de conflicto de concurrencia de los endpoints a medida de Ventas
 * (contrato FD-10).
 */
export const ROW_VERSION_CONFLICT_CODE = 'ROW_VERSION_CONFLICT';

/**
 * Códigos de negocio que SÍ representan un conflicto de concurrencia optimista.
 * Debe reflejar `isOptimisticConcurrencyError` de la app consumidora
 * (`apps/frontend/src/config/document-conflict.ts`).
 */
export const CONCURRENCY_CODES = [OPTIMISTIC_LOCK_CODE, ROW_VERSION_CONFLICT_CODE] as const;

export interface ConflictDetails {
  /** Mensaje traducido (problem-details `detail`/`title`) o el del error. */
  message?: string;
  /** `row_version` que el cliente envió (extensión `row_version_expected`). */
  expected?: string | number;
  /** `row_version` real en el servidor (extensión `row_version_actual`). */
  actual?: string | number;
}

/** Extrae un valor de cualquiera de las claves dadas en un objeto plano. */
function pick(obj: unknown, keys: string[]): string | number | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' || typeof v === 'number') return v;
  }
  return undefined;
}

/**
 * Inspecciona un valor lanzado por una mutación y, si corresponde a un conflicto
 * de concurrencia optimista, devuelve los detalles para el modal y el log.
 * Devuelve `null` si NO es un conflicto.
 *
 * Es conflicto de concurrencia solo cuando el `code` de negocio es uno de
 * concurrencia conocido (`OPTIMISTIC_LOCK_VIOLATION` / `ROW_VERSION_CONFLICT`),
 * o cuando es un 409 SIN `code` de negocio. Cualquier 409 con `code` de negocio
 * (p.ej. `NegativeCostException`) NO es conflicto y devuelve `null` para que el
 * kit lo enrute a su manejo de error normal / banner.
 */
export function isConflictError(err: unknown): ConflictDetails | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as Record<string, unknown>;

  // status puede vivir en el error o en una respuesta anidada (axios-like).
  const response = e.response as Record<string, unknown> | undefined;
  const status = e.status ?? e.statusCode ?? response?.status;

  // body: problem-details. Puede venir en `.body`, `.data` o `response.data`.
  const body = (e.body ?? e.data ?? response?.data ?? e.problem) as
    | Record<string, unknown>
    | undefined;

  const code = e.code ?? body?.code ?? body?.type;
  const codeStr = typeof code === 'string' ? code : '';

  // El backend del ERP Comercial responde 409 para CUALQUIER DomainException de
  // negocio (NegativeCostException, reglas de secuencia/prefijo, duplicados,
  // integridad, ...), no solo para concurrencia. Por eso un 409 crudo NO basta:
  // es conflicto de concurrencia solo cuando trae un `code` de concurrencia
  // conocido, o cuando es un 409 SIN `code` de negocio (p.ej. la
  // DbUpdateConcurrencyException que se serializa sin `code`).
  const isConcurrencyCode =
    !!codeStr && CONCURRENCY_CODES.some((c) => codeStr === c || codeStr.includes(c));

  const isConflict = isConcurrencyCode || (status === 409 && !codeStr);

  if (!isConflict) return null;

  // Las extensiones de problem-details pueden estar al tope del body o anidadas.
  const extensions = (body?.extensions ?? body) as Record<string, unknown> | undefined;
  const expected = pick(extensions, ['row_version_expected', 'rowVersionExpected']);
  const actual = pick(extensions, ['row_version_actual', 'rowVersionActual']);

  const detail = body?.detail ?? body?.title;
  const message =
    typeof detail === 'string'
      ? detail
      : typeof e.message === 'string'
        ? e.message
        : undefined;

  return { message, expected, actual };
}
